<template>
  <div
    class="export-button"
    :class="exportButtonClasses"
  >
    <universal-button
      v-if="!isExpanded"
      :class="isMobile ? 'export-button__main' : 'export-button__main mr-2'"
      type="base"
      :label="buttonLabel"
      :color="mainButtonColor"
      font-color="#FFFFFF"
      prepend-icon="mdi-file-document"
      :prepend-icon-style="{
        fontSize: '17px',
      }"
      :custom-class="'export-button__main-btn'"
      @click="openExportControls"
    />

    <div
      v-else
      class="export-button__controls"
    >
      <select-field
        v-model="selectedFormat"
        class="export-button__select"
        :config="selectConfig"
      />

      <universal-button
        class="export-button__cancel"
        type="cancel"
        :label="cancelLabel"
        :color="cancelButtonColor"
        :font-color="'#FFFFFF'"
        :custom-class="'export-button__cancel-btn'"
        @click="cancelExport"
      />

      <universal-button
        class="mr-2"
        type="base"
        append-icon="mdi-download"
        append-icon-color="#FFFFFF"
        :color="mainButtonColor"
        :font-color="'#FFFFFF'"
        :disabled="!selectedFormat"
        @click="confirmExport"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import UniversalButton from '@/global/components/buttons/UniversalButton.vue'
import SelectField from '@/global/components/fields/SelectField.vue'
import { isArray, isFunction } from '@/global/utils/utils'
import { useBreakpoint } from '@/global/composables/useBreakpoint'

const { isMobile } = useBreakpoint()

const props = defineProps({
  config: {
    type: Object,
    default: () => ({})
  }
})

const DEFAULT_OPTIONS = ['csv', 'pdf', 'xlsx']

const isExpanded = ref(false)
const selectedFormat = ref(null)

const selectItems = computed(() => {
  const options = isArray(props.config?.options) && props.config.options.length
    ? props.config.options
    : DEFAULT_OPTIONS

  return options
    .map((option) => {
      if (typeof option === 'string') {
        return {
          name: option,
          id: option
        }
      }

      const name = option?.name || option?.text || option?.label || option?.value || ''
      const id = option?.id || option?.value || option?.name || option?.text || option?.label || ''

      return {
        name,
        id
      }
    })
    .filter(option => option.id)
})

const defaultFormat = computed(() => selectItems.value[0]?.id || 'csv')

const buttonLabel = computed(() => props.config?.buttonLabel || 'Izvoz Datoteke')
const selectLabel = computed(() => props.config?.selectLabel || 'Format datoteke')
const cancelLabel = computed(() => props.config?.cancelLabel || 'ODUSTANI')
const mainButtonColor = computed(() => props.config?.mainButtonColor || '#1F2937')
const cancelButtonColor = computed(() => props.config?.cancelButtonColor || '#5F5F5F')
const mobileControlsDirection = computed(() => props.config?.mobileControlsDirection || 'column')
const isMobileControlsRow = computed(() => mobileControlsDirection.value === 'row')
const mobileFullWidth = computed(() => props.config?.mobileFullWidth !== false)
const exportButtonClasses = computed(() => ({
  'export-button--mobile': isMobile.value,
  'export-button--mobile-row': isMobile.value && isMobileControlsRow.value,
  'export-button--mobile-column': isMobile.value && !isMobileControlsRow.value,
  'export-button--mobile-full-width': isMobile.value && mobileFullWidth.value
}))

const selectConfig = computed(() => ({
  label: selectLabel.value,
  items: selectItems.value,
  dense: true,
  outlined: true,
  color: '#8EA5BF',
  class: 'select-field-custom-class'
}))

function ensureSelectedFormat () {
  const allowedValues = selectItems.value.map(item => item.id)
  if (!allowedValues.includes(selectedFormat.value)) {
    selectedFormat.value = defaultFormat.value
  }
}

function openExportControls () {
  isExpanded.value = true
  ensureSelectedFormat()
}

function closeExportControls (resetSelection = false) {
  isExpanded.value = false
  if (resetSelection) {
    selectedFormat.value = null
  }
}

async function confirmExport () {
  ensureSelectedFormat()

  const onConfirm = props.config?.onConfirm
  try {
    if (isFunction(onConfirm)) {
      await Promise.resolve(onConfirm(selectedFormat.value))
    }
  }
  finally {
    closeExportControls(true)
  }
}

function cancelExport () {
  closeExportControls(true)
}
</script>

<style scoped lang="scss">
.export-button {
  display: inline-flex;
  align-items: flex-start;
}

.export-button__main {
  min-width: 0;
}

.export-button__controls {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.export-button--mobile {
  display: flex;
  flex-direction: column;
  align-items: stretch;
}

.export-button--mobile .export-button__main {
  min-width: 0;
  width: 100%;
  margin-right: 0 !important;
}

.export-button--mobile-full-width {
  width: 100%;
}

.export-button--mobile-column .export-button__controls {
  flex-direction: column;
  width: 100%;
}

.export-button--mobile-column .export-button__select,
.export-button--mobile-column .export-button__cancel,
.export-button--mobile-column .export-button__controls > .mr-2 {
  width: 100%;
  min-width: 0;
  max-width: none;
}

.export-button--mobile-row .export-button__controls {
  flex-direction: row;
  flex-wrap: wrap;
  width: 100%;
}

.export-button--mobile-row .export-button__select {
  flex: 1 1 180px;
  min-width: 0;
  max-width: none;
}

.export-button--mobile-row .export-button__cancel,
.export-button--mobile-row .export-button__controls > .mr-2 {
  flex: 0 0 auto;
  width: auto;
  min-width: 0;
  max-width: none;
}

.export-button__select {
  min-width: 176px;
  max-width: 176px;
  font-family: 'Satoshi', sans-serif;
}

.export-button__cancel {
  min-width: 96px;
}

::v-deep {
  .export-button__main-btn,
  .export-button__cancel-btn {
    letter-spacing: 0;
    font-family: 'Satoshi', sans-serif !important;
    font-size: 12px !important;
    font-weight: 500 !important;
  }

  .export-button__select .v-input__slot {
    min-height: 32px !important;
  }

  .export-button__select .v-label {
    color: #8EA5BF !important;
    font-family: 'Satoshi', sans-serif;
    font-size: 12px;
    letter-spacing: 0;
  }

  .export-button__select .v-select__selection {
    font-family: 'Satoshi', sans-serif;
    font-size: 13px;
    color: #111827;
    letter-spacing: 0;
  }

  .select-field-custom-class {
    margin-top: unset !important;
  }
}
</style>
